import React, { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Proactive layer for the stale-chunk problem. The service worker precaches
// each build; with registerType: 'prompt' a new deploy installs a waiting SW
// rather than silently swapping under the user's feet. We surface a dismissible
// toast and let the user reload when convenient — friendlier than an automatic
// mid-task reload, and it means long-lived tabs update *before* tripping over a
// missing lazy chunk.
//
// registration.update() polls the server for a new SW (and therefore a new
// precache manifest) without reloading, so a tab left open overnight still
// notices the deploy.
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

export const PwaUpdatePrompt: React.FC = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_CHECK_INTERVAL_MS);
    },
  });

  const [reloading, setReloading] = useState(false);

  if (!needRefresh) return null;

  // updateServiceWorker(true) only messages the *waiting* SW to skipWaiting;
  // vite-plugin-pwa reloads the page on the resulting `controlling` event, but
  // ONLY when that event is flagged as an update (i.e. the page was already
  // SW-controlled). If the page isn't controlled (e.g. after a hard reload,
  // controller === null) or the waiting worker has gone, no reload ever fires
  // and the toast sits there doing nothing on click. Guarantee a reload with a
  // short fallback: the happy-path reload unloads the page first and cancels
  // this timer, so it only fires when vite-plugin-pwa wouldn't have.
  //
  // Either path leaves a gap (skipWaiting round-trip, or the fallback ceiling)
  // where the page looks frozen, so flip into a `reloading` state on click for
  // immediate feedback and to block a duplicate reload from a second click.
  const handleReload = () => {
    if (reloading) return;
    setReloading(true);
    updateServiceWorker(true);
    setTimeout(() => window.location.reload(), 1500);
  };

  return (
    <div className="fixed inset-x-0 bottom-4 z-[9999] flex justify-center px-4">
      <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
        <span className="text-sm font-medium text-gray-800">
          A new version is available.
        </span>
        <button
          type="button"
          onClick={handleReload}
          disabled={reloading}
          className="flex items-center gap-2 rounded-full bg-gray-900 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-default disabled:hover:bg-gray-900"
        >
          {reloading && (
            <svg
              className="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          )}
          {reloading ? 'Reloading…' : 'Reload'}
        </button>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          disabled={reloading}
          className="text-sm text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-40 disabled:hover:text-gray-400"
        >
          Later
        </button>
      </div>
    </div>
  );
};
